import type { CategoryId, Difficulty, RoundResult } from "./types";

export interface HighscoreEntry {
  id?: number;
  categoryId: CategoryId;
  difficulty: Difficulty;
  weekKey?: string;
  name: string;
  score: number;
  correctCount: number;
  wrongCount: number;
  date: string;
}

export interface DuelRecord {
  id?: number;
  winner: string | null;
  p1: RoundResult;
  p2: RoundResult;
  date: string;
}

/**
 * Calculates the ISO week start key (Monday YYYY-MM-DD format).
 * Mondays 00:00:00 through Sundays 23:59:59 belong to the exact same week key.
 */
export function getWeekKey(d: Date = new Date()): string {
  const date = new Date(d.getTime());
  const day = date.getDay(); // 0 is Sunday, 1 is Monday, ..., 6 is Saturday
  const diff = date.getDate() - (day === 0 ? 6 : day - 1);
  const monday = new Date(date.setDate(diff));
  const year = monday.getFullYear();
  const month = String(monday.getMonth() + 1).padStart(2, "0");
  const dayOfMonth = String(monday.getDate()).padStart(2, "0");
  return `${year}-${month}-${dayOfMonth}`;
}

export function getWeekDateRange(weekKey: string): { start: Date; end: Date } {
  const [y, m, d] = (weekKey || getWeekKey()).split("-").map(Number);
  const start = new Date(y || 2026, (m || 1) - 1, d || 1, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

const THAI_MONTHS = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."
];

export function formatWeekLabel(weekKey: string, currentWeekKey: string = getWeekKey()): string {
  if (!weekKey) return "สัปดาห์ปัจจุบัน";
  const { start, end } = getWeekDateRange(weekKey);
  const isCurrent = weekKey === currentWeekKey;

  const startStr = `${start.getDate()} ${THAI_MONTHS[start.getMonth()]}`;
  const endStr = `${end.getDate()} ${THAI_MONTHS[end.getMonth()]} ${end.getFullYear()}`;
  const rangeStr = `${startStr} – ${endStr}`;

  return isCurrent ? `🗓️ สัปดาห์นี้ (${rangeStr})` : `📅 สัปดาห์ ${rangeStr}`;
}

const DB_NAME = "MathWarDB";
const DB_VERSION = 1;
const SOLO_STORE = "solo_highscores";
const DUEL_STORE = "duel_records";
const LAST_NAME_KEY = "mathwar:lastName";

/* IndexedDB helpers */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not supported"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(SOLO_STORE)) {
        db.createObjectStore(SOLO_STORE, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(DUEL_STORE)) {
        db.createObjectStore(DUEL_STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Loads list of all available weeks with highscore records.
 */
export async function loadAvailableWeeks(): Promise<string[]> {
  const currentWeek = getWeekKey();
  const weekSet = new Set<string>();
  weekSet.add(currentWeek);

  try {
    const res = await fetch("/api/highscores?action=weeks");
    if (res.ok) {
      const data = (await res.json()) as string[];
      if (Array.isArray(data)) {
        data.forEach((w) => {
          if (typeof w === "string" && w) weekSet.add(w);
        });
      }
    }
  } catch (err) {
    console.error("[MathWar Storage] D1 loadAvailableWeeks error:", err);
  }

  const localWeeks = await loadLocalAvailableWeeks();
  localWeeks.forEach((w) => weekSet.add(w));

  const list = Array.from(weekSet);
  list.sort((a, b) => b.localeCompare(a));
  return list;
}

/**
 * Loads Top 10 Highscores for a given category, difficulty, and week.
 * Primary: Cloudflare D1 via Pages Function API (/api/highscores)
 * Fallback: Local IndexedDB / LocalStorage
 */
export async function loadHighscores(
  categoryId: CategoryId,
  difficulty: Difficulty,
  weekKey: string = getWeekKey(),
): Promise<HighscoreEntry[]> {
  try {
    const res = await fetch(
      `/api/highscores?categoryId=${encodeURIComponent(categoryId)}&difficulty=${encodeURIComponent(difficulty)}&weekKey=${encodeURIComponent(weekKey)}`,
    );
    if (res.ok) {
      const data = (await res.json()) as HighscoreEntry[];
      if (Array.isArray(data)) {
        return data;
      }
    } else {
      const errText = await res.text();
      console.error("[MathWar Storage] D1 loadHighscores error:", res.status, errText);
    }
  } catch (err) {
    console.error("[MathWar Storage] D1 loadHighscores network/API error:", err);
  }

  return loadLocalHighscores(categoryId, difficulty, weekKey);
}

export async function saveHighscore(entry: HighscoreEntry): Promise<HighscoreEntry[]> {
  const preparedEntry: HighscoreEntry = {
    ...entry,
    weekKey: entry.weekKey || getWeekKey(new Date(entry.date)),
  };

  saveLocalHighscoreEntry(preparedEntry).catch(() => {});

  try {
    const res = await fetch("/api/highscores", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(preparedEntry),
    });
    if (res.ok) {
      const data = (await res.json()) as HighscoreEntry[];
      if (Array.isArray(data)) {
        return data;
      }
    } else {
      const errText = await res.text();
      console.error("[MathWar Storage] D1 saveHighscore error:", res.status, errText);
    }
  } catch (err) {
    console.error("[MathWar Storage] D1 saveHighscore network/API error:", err);
  }

  return loadLocalHighscores(preparedEntry.categoryId, preparedEntry.difficulty, preparedEntry.weekKey);
}

export async function loadDuelRecords(): Promise<DuelRecord[]> {
  try {
    const res = await fetch("/api/duels");
    if (res.ok) {
      const data = (await res.json()) as DuelRecord[];
      if (Array.isArray(data)) {
        return data;
      }
    } else {
      const errText = await res.text();
      console.error("[MathWar Storage] D1 loadDuelRecords error:", res.status, errText);
    }
  } catch (err) {
    console.error("[MathWar Storage] D1 loadDuelRecords network/API error:", err);
  }

  return loadLocalDuelRecords();
}

export async function saveDuelRecord(record: DuelRecord): Promise<DuelRecord[]> {
  saveLocalDuelRecord(record).catch(() => {});

  try {
    const res = await fetch("/api/duels", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(record),
    });
    if (res.ok) {
      const data = (await res.json()) as DuelRecord[];
      if (Array.isArray(data)) {
        return data;
      }
    } else {
      const errText = await res.text();
      console.error("[MathWar Storage] D1 saveDuelRecord error:", res.status, errText);
    }
  } catch (err) {
    console.error("[MathWar Storage] D1 saveDuelRecord network/API error:", err);
  }

  return loadLocalDuelRecords();
}

/* Local Storage & IndexedDB Fallback Logic with Weekly & Same-Name Deduplication */

function filterAndDeduplicateLocalHighscores(
  allItems: HighscoreEntry[],
  categoryId: CategoryId,
  difficulty: Difficulty,
  targetWeekKey: string = getWeekKey(),
): HighscoreEntry[] {
  const filtered = allItems.filter((item) => {
    const itemWeek = item.weekKey || getWeekKey(new Date(item.date));
    return (
      item.categoryId === categoryId &&
      item.difficulty === difficulty &&
      (itemWeek === targetWeekKey || !itemWeek)
    );
  });

  const playerMap = new Map<string, HighscoreEntry>();
  for (const item of filtered) {
    const nameKey = (item.name || "").trim().toLowerCase();
    if (!nameKey) continue;
    const existing = playerMap.get(nameKey);
    if (!existing || item.score > existing.score) {
      playerMap.set(nameKey, item);
    }
  }

  const list = Array.from(playerMap.values());
  list.sort((a, b) => b.score - a.score);
  return list.slice(0, 10);
}

async function loadLocalHighscores(
  categoryId: CategoryId,
  difficulty: Difficulty,
  targetWeekKey: string = getWeekKey(),
): Promise<HighscoreEntry[]> {
  try {
    const db = await openDB();
    return new Promise<HighscoreEntry[]>((resolve) => {
      const tx = db.transaction(SOLO_STORE, "readonly");
      const store = tx.objectStore(SOLO_STORE);
      const req = store.getAll();
      req.onsuccess = () => {
        const allItems = (req.result as HighscoreEntry[]) || [];
        resolve(filterAndDeduplicateLocalHighscores(allItems, categoryId, difficulty, targetWeekKey));
      };
      req.onerror = () => resolve(loadLocalStorageHighscores(categoryId, difficulty, targetWeekKey));
    });
  } catch {
    return loadLocalStorageHighscores(categoryId, difficulty, targetWeekKey);
  }
}

async function saveLocalHighscoreEntry(entry: HighscoreEntry): Promise<void> {
  const entryWeek = entry.weekKey || getWeekKey(new Date(entry.date));
  const fullEntry: HighscoreEntry = { ...entry, weekKey: entryWeek };

  try {
    const db = await openDB();
    const allItems = await new Promise<HighscoreEntry[]>((resolve) => {
      const tx = db.transaction(SOLO_STORE, "readonly");
      const store = tx.objectStore(SOLO_STORE);
      const req = store.getAll();
      req.onsuccess = () => resolve((req.result as HighscoreEntry[]) || []);
      req.onerror = () => resolve(loadLocalStorageAllHighscores());
    });

    const targetNameKey = fullEntry.name.trim().toLowerCase();
    const updatedItems = allItems.filter((item) => {
      const itemWeek = item.weekKey || getWeekKey(new Date(item.date));
      const sameSetting =
        item.categoryId === fullEntry.categoryId &&
        item.difficulty === fullEntry.difficulty &&
        itemWeek === entryWeek;
      const sameName = item.name.trim().toLowerCase() === targetNameKey;
      return !(sameSetting && sameName);
    });

    updatedItems.push(fullEntry);

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(SOLO_STORE, "readwrite");
      const store = tx.objectStore(SOLO_STORE);
      store.clear().onsuccess = () => {
        let added = 0;
        if (updatedItems.length === 0) resolve();
        updatedItems.forEach((item) => {
          const addReq = store.add(item);
          addReq.onsuccess = () => {
            added++;
            if (added === updatedItems.length) resolve();
          };
          addReq.onerror = () => reject(addReq.error);
        });
      };
    });

    saveLocalStorageAllHighscores(updatedItems);
  } catch {
    const all = [...loadLocalStorageAllHighscores(), fullEntry];
    saveLocalStorageAllHighscores(all);
  }
}

async function loadLocalDuelRecords(): Promise<DuelRecord[]> {
  try {
    const db = await openDB();
    return new Promise<DuelRecord[]>((resolve) => {
      const tx = db.transaction(DUEL_STORE, "readonly");
      const store = tx.objectStore(DUEL_STORE);
      const req = store.getAll();
      req.onsuccess = () => {
        const items = (req.result as DuelRecord[]) || [];
        items.reverse();
        resolve(items.slice(0, 10));
      };
      req.onerror = () => resolve(loadLocalStorageDuelRecords());
    });
  } catch {
    return loadLocalStorageDuelRecords();
  }
}

async function saveLocalDuelRecord(record: DuelRecord): Promise<void> {
  try {
    const db = await openDB();
    const currentList = await loadLocalDuelRecords();
    const updated = [record, ...currentList].slice(0, 10);

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(DUEL_STORE, "readwrite");
      const store = tx.objectStore(DUEL_STORE);
      store.clear().onsuccess = () => {
        let added = 0;
        if (updated.length === 0) resolve();
        updated.forEach((item) => {
          const addReq = store.add(item);
          addReq.onsuccess = () => {
            added++;
            if (added === updated.length) resolve();
          };
          addReq.onerror = () => reject(addReq.error);
        });
      };
    });

    saveLocalStorageDuelRecords(updated);
  } catch {
    const list = [record, ...loadLocalStorageDuelRecords()].slice(0, 10);
    saveLocalStorageDuelRecords(list);
  }
}

/* LocalStorage fallback helpers */
function loadLocalStorageAllHighscores(): HighscoreEntry[] {
  try {
    const raw = localStorage.getItem("mathwar:highscores");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function loadLocalStorageHighscores(
  categoryId: CategoryId,
  difficulty: Difficulty,
  targetWeekKey: string = getWeekKey(),
): HighscoreEntry[] {
  const all = loadLocalStorageAllHighscores();
  return filterAndDeduplicateLocalHighscores(all, categoryId, difficulty, targetWeekKey);
}

function saveLocalStorageAllHighscores(list: HighscoreEntry[]): void {
  try {
    localStorage.setItem("mathwar:highscores", JSON.stringify(list));
  } catch {
    // ignore
  }
}

function loadLocalStorageDuelRecords(): DuelRecord[] {
  try {
    const raw = localStorage.getItem("mathwar:duels");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLocalStorageDuelRecords(list: DuelRecord[]): void {
  try {
    localStorage.setItem("mathwar:duels", JSON.stringify(list));
  } catch {
    // ignore
  }
}

async function loadLocalAvailableWeeks(): Promise<string[]> {
  const weekSet = new Set<string>();
  weekSet.add(getWeekKey());

  try {
    const db = await openDB();
    const allItems = await new Promise<HighscoreEntry[]>((resolve) => {
      const tx = db.transaction(SOLO_STORE, "readonly");
      const store = tx.objectStore(SOLO_STORE);
      const req = store.getAll();
      req.onsuccess = () => resolve((req.result as HighscoreEntry[]) || []);
      req.onerror = () => resolve(loadLocalStorageAllHighscores());
    });
    allItems.forEach((item) => {
      const w = item.weekKey || getWeekKey(new Date(item.date));
      if (w) weekSet.add(w);
    });
  } catch {
    const all = loadLocalStorageAllHighscores();
    all.forEach((item) => {
      const w = item.weekKey || getWeekKey(new Date(item.date));
      if (w) weekSet.add(w);
    });
  }

  return Array.from(weekSet);
}

export function loadLastName(): string {
  try {
    return localStorage.getItem(LAST_NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

export function saveLastName(name: string): void {
  try {
    localStorage.setItem(LAST_NAME_KEY, name);
  } catch {
    // ignore
  }
}
